"use client";

import React, { useState, useMemo, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import Step1 from "./Step1";
import Step2Race from "./Step2Race";
import Step3Team from "./Step3Team";
import Step3Individual from "./Step3Individual";
import PaymentStep from "./PaymentStep";
import ProgressBar from "./ProgressStep";
import PaymentStatus from "./payment/PaymentStatus";
import cashfree from "../../services/Cashfree";
import { useDispatch } from "react-redux";
import sanawarLogo from "../../img/Parallax/sanawar-logo.png";
import sanawarBg from "../../img/Parallax/form-bg.jpg";
import {
  createPaymentIntentAsync,
  submitRegistrationAsync,
} from "../../features/sanawar/sanawarSlice";
import { getPaymentStatus } from "../../features/sanawar/sanawarAPI";
import { HourglassTop } from "@mui/icons-material";

export default function TourRegistrationForm() {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);

  // coupon related
  const [coupon, setCoupon] = useState(null);
  const [finalAmount, setFinalAmount] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    control,
    resetField,
    getValues,
    setValue,
  } = useForm();

  const participationType = watch("participationType");
  const participationCategory = watch("participationCategory");

  const participationTypeMounted = useRef(false);
  const participationCategoryMounted = useRef(false);

  useEffect(() => {
    if (!participationTypeMounted.current) {
      participationTypeMounted.current = true;
      return;
    }
    resetField("bloodGroup");
    resetField("tshirtSize");
    resetField("emergencyContact");
    resetField("participationCategory");
    resetField("officialSchoolTeam");
    resetField("raceCategory");
  }, [participationType, resetField]);

  useEffect(() => {
    if (!participationCategoryMounted.current) {
      participationCategoryMounted.current = true;
      return;
    }
    resetField("officialSchoolTeam");
    resetField("raceCategory");
  }, [participationCategory, resetField]);

  const baseAmount = useMemo(() => {
    if (participationType === "spectator") return 199;
    if (participationType === "race") {
      if (participationCategory === "team") return 299;
      return 499;
    }
    return 0;
  }, [participationType, participationCategory]);

  // sync base amount into final amount on type/category change
  useEffect(() => {
    setFinalAmount(baseAmount);
  }, [baseAmount]);

  const next = () => {
    if (step === 1 && participationType === "spectator") setStep(4);
    else setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const onSubmit = async () => {
    // Prevent direct form submission; payment is initiated via the Pay button
  };

  // ✅ handle apply coupon
  const handleApplyCoupon = (newAmount, appliedCoupon) => {
    setFinalAmount(newAmount);
    setCoupon(appliedCoupon);
  };

  // ✅ handle pay or free registration
  const handlePayClick = async (
    payAmount = finalAmount,
    appliedCoupon = coupon
  ) => {
    const phone = getValues("primaryContact");
    if (!phone) {
      alert("Please enter your contact number.");
      return;
    }

    // Free registration (skip payment)
    if (payAmount <= 0) {
      try {
        const formData = getValues();
        await dispatch(
          submitRegistrationAsync({
            ...formData,
            Coupons: appliedCoupon, // 👈 send coupon in API
            paymentID: null,
          })
        ).unwrap();

        setPaymentSuccess(true);
        setPaymentDetails({ message: "Free registration successful" });
      } catch (err) {
        setPaymentSuccess(false);
        setPaymentDetails({ message: "Registration failed" });
      }
      return;
    }

    // Normal paid registration
    try {
      localStorage.setItem("tds_primaryContact", String(phone));

      const resultAction = await dispatch(
        createPaymentIntentAsync({ phone, amount: payAmount })
      ).unwrap();

      setSessionInfo(resultAction);
      setValue("paymentID", resultAction?.LinkID);

      setModalOpen(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Payment initiation failed");
    }
  };

  // 👇 Add this near the top of your component (outside useEffect)
  const hasRegisteredRef = useRef(false);

  useEffect(() => {
    if (sessionInfo?.Payment_Session_ID && sessionInfo?.LinkID) {
      let isMobile = window.innerWidth <= 768;

      const checkoutOptions = {
        paymentSessionId: sessionInfo.Payment_Session_ID,
        redirectTarget: document.getElementById("cashfree-payment"),
        container: "_blank",
        appearance: {
          width: isMobile ? "100vw" : "650px",
          height: isMobile ? "100vh" : "600px",
        },
        openInNewTab: true,
      };

      // ✅ Start payment checkout (async)
      cashfree?.checkout(checkoutOptions).then((result) => {
        console.log("Cashfree checkout result:", result);
      });

      // ✅ Start polling for status every 5 seconds
      let intervalId;
      const startTime = Date.now();

      const pollPaymentStatus = async () => {
        try {
          const status = await getPaymentStatus(sessionInfo?.LinkID);
          const paymentStatuses = status?.paymentStatuses || [];
          const isSuccess = paymentStatuses.some(
            (s) => s?.toUpperCase() === "SUCCESS"
          );

          if (isSuccess && !hasRegisteredRef.current) {
            // ✅ Prevent duplicate registration calls
            hasRegisteredRef.current = true;
            clearInterval(intervalId);

            setPaymentSuccess(true);
            setPaymentDetails(status);

            // Register user immediately
            const formData = getValues();

            dispatch(
              submitRegistrationAsync({
                ...formData,
                Coupons: coupon,
                paymentID: sessionInfo?.LinkID,
              })
            )
              .unwrap()
              .then((res) => {
                console.log("✅ Registration submitted successfully:", res);
              })
              .catch((err) => {
                console.error("❌ Registration submission failed:", err);
              });
          } else if (!isSuccess) {
            console.log("Still waiting for payment success...");
          }

          // ⏱️ Stop polling after 5 minutes to prevent infinite checks
          if (Date.now() - startTime > 300000) {
            clearInterval(intervalId);
            console.warn("Stopped polling after 5 minutes");
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      };

      // Poll every 5 seconds
      intervalId = setInterval(pollPaymentStatus, 3000);

      // Cleanup on unmount
      return () => clearInterval(intervalId);
    }
  }, [dispatch, sessionInfo, getValues, coupon]);

  return (
    <div
      className="w-full text-black-1 min-h-screen bg-white-1 rounded-lg shadow-lg relative"
      style={{
        backgroundImage: `url(${sanawarBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Logo (Non-sticky on mobile, appears above progress bar) */}
      <div className="w-full flex justify-center items-center bg-white-1 py-2 md:hidden">
        <img src={sanawarLogo} alt="Website Logo" className="h-14 w-auto" />
      </div>

      {/* Progress bar (Sticky on all views, contains logo inline on desktop only) */}
      <div className="w-full sticky top-0 left-0 bg-white-1 flex flex-col md:flex-row items-center shadow-md justify-between z-100 px-0 md:px-4">
        {/* Logo for desktop view */}
        <div className="max-sm:hidden flex items-center">
          <img
            src={sanawarLogo}
            alt="Website Logo"
            className="h-16 w-auto md:h-20"
          />
        </div>

        {/* Progress bar */}
        <div className="flex-1 flex justify-center">
          <div className="w-full md:w-[60vw] py-3 h-fit">
            <ProgressBar currentStep={step} />
          </div>
        </div>
      </div>

      {/* Form & payment container */}
      <div className="w-full mt-5 relative max-sm:pb-5">
        {!modalOpen && paymentSuccess === null && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {step === 1 && (
              <Step1 register={register} control={control} next={next} />
            )}
            {step === 2 && participationType === "race" && (
              <Step2Race control={control} prev={prev} next={next} />
            )}
            {step === 3 && participationCategory === "team" && (
              <Step3Team
                control={control}
                setValue={setValue}
                prev={prev}
                next={next}
              />
            )}
            {step === 3 && participationCategory === "individual" && (
              <Step3Individual control={control} prev={prev} next={next} />
            )}
            {step === 4 && (
              <PaymentStep
                amount={baseAmount}
                prev={prev}
                participationType={participationType}
                setStep={setStep}
                onPayClick={handlePayClick}
                userData={{
                  firstName: getValues("firstName"),
                  lastName: getValues("lastName"),
                  email: getValues("email"),
                  primaryContact: getValues("primaryContact"),
                  participationType,
                }}
                onApplyCoupon={handleApplyCoupon}
              />
            )}
          </form>
        )}

        {(modalOpen || paymentSuccess !== null) && (
          <div className="w-full min-h-screen sm:h-[600px] max-w-full md:max-w-[650px] mx-auto rounded-lg shadow-lg relative bg-white-1">
            {modalOpen && paymentSuccess === null && (
              <>
                <div className="absolute inset-0 flex justify-center items-center z-0">
                  <HourglassTop
                    fontSize="large"
                    className="animate-spin text-blue"
                  />
                </div>
                <div
                  id="cashfree-payment"
                  className="w-full h-full md:w-[650px] md:h-[600px]"
                  style={{ borderRadius: "10px" }}
                ></div>
              </>
            )}

            {paymentSuccess !== null && (
              <PaymentStatus
                success={paymentSuccess}
                amount={finalAmount} // 👈 show discounted amount
                details={paymentDetails || undefined}
                userData={getValues()}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
